import { API_AUTH } from "./constants";

// ─── Cache config ─────────────────────────────────────────────
// Only these actions are cached; everything else is a pass-through.
const READ_ACTIONS = new Set(["getRequests", "getQuotes", "getOrders"]);

// Any of these actions mutate data → wipe the cache afterward so the
// next read always gets fresh data from the server.
const WRITE_ACTIONS = new Set([
  "submitRequest",
  "createQuote", "updateQuoteStatus",
  "submitOrder", "updateOrderStatus", "updateOrderField", "uploadOrderFile",
  "updateAccount", "updatePassword", "setupAccount",
]);

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function cacheKey(action, data) {
  // Scope cache per user + role so switching accounts never leaks data
  return `boal_cache_${action}_${data.email || ""}_${data.role || ""}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, value } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) { sessionStorage.removeItem(key); return null; }
    return value;
  } catch { return null; }
}

function writeCache(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), value })); } catch { /* quota exceeded — ignore */ }
}

function clearCache() {
  try {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith("boal_cache_"))
      .forEach(k => sessionStorage.removeItem(k));
  } catch { /* ignore */ }
}

// ─── Main export ──────────────────────────────────────────────
export async function apiCall(action, data = {}) {
  // Return cached response immediately for read actions
  if (READ_ACTIONS.has(action)) {
    const key = cacheKey(action, data);
    const cached = readCache(key);
    if (cached) return cached;

    const res = await fetch(API_AUTH, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ action, ...data }),
    });
    const json = await res.json();
    if (json.success) writeCache(key, json);
    return json;
  }

  // Execute write; invalidate cache so next read is always fresh
  const res = await fetch(API_AUTH, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action, ...data }),
  });
  const json = await res.json();
  if (WRITE_ACTIONS.has(action)) clearCache();
  return json;
}
